import { useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useSubscription } from './useSubscription';

export function useFeatureAccess() {
  const { subscription, initialized, initializeSubscription } = useSubscription();

  // Make sure subscription is initialized
  useEffect(() => {
    if (!initialized) {
      initializeSubscription();
    }
  }, [initialized, initializeSubscription]);

  const incrementUsage = useCallback(async (feature: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.rpc('increment_feature_usage', {
        p_user_id: user.id,
        p_feature: feature
      });
    } catch (err) {
      console.error('Error incrementing feature usage:', err);
    }
  }, []);

  const checkFeatureAccess = useCallback(async (feature: string) => {
    // Ensure subscription is initialized before checking
    if (!initialized) {
      await initializeSubscription();
    }
    
    if (!subscription) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase.rpc('check_feature_access', {
        p_user_id: user.id,
        p_feature: feature
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return false;
    }
  }, [subscription, initialized, initializeSubscription]);

  return {
    incrementUsage,
    checkFeatureAccess,
    isSubscribed: subscription?.status === 'active',
    tier: subscription?.tier || 'free'
  };
}